import { useAuth } from '@modules/auth/hooks';
import { FC, ReactNode, useEffect } from 'react';
import { useAuthStore } from '@modules/auth/store';
import { partnerClient, oktaClient } from '@modules/core/services';
import { AxiosInstance } from 'axios';

export const makeRequestInterceptor = (accessToken: string | undefined) => {
  return (config: any) => {
    if (config.headers !== undefined && !Object.hasOwn(config.headers, 'Authorization') && accessToken !== undefined) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  };
};

export const makeErrorResponseInterceptor = (signOut: () => Promise<void>) => {
  return async (error: any) => {
    const status = error?.response?.status;

    if (status === 401 || status === 403) {
      await signOut();
    }

    return await Promise.reject(error);
  };
};

const ClientInterceptors: FC<{ children: ReactNode }> = ({ children }) => {
  const { authService, accessToken } = useAuth();
  const { logout: authStoreLogout } = useAuthStore();

  const addRequestInterceptor = (client: AxiosInstance): number => {
    return client.interceptors.request.use(makeRequestInterceptor(accessToken), async (error: unknown) => {
      return await Promise.reject(error).catch(() => {});
    });
  };

  const addResponseIncerceptor = (client: AxiosInstance): number => {
    const logout = async (): Promise<void> =>
      await authService
        .signOut()
        .then(() => {
          authService.clearStorage();
          authStoreLogout();
        })
        .catch((error) => {
          throw new Error(error);
        });

    return client.interceptors.response.use((response: any) => {
      return response?.data || response;
    }, makeErrorResponseInterceptor(logout));
  };

  const removeRequestInterceptor = (requestInterceptor: number, client: AxiosInstance): void => {
    client.interceptors.request.eject(requestInterceptor);
  };

  const removeResponseInterceptor = (responseInterceptor: number, client: AxiosInstance): void => {
    client.interceptors.response.eject(responseInterceptor);
  };

  useEffect(() => {
    let requestInterceptor: number;
    let responseInterceptor: number;
    let oktaRequestInterceptor: number;
    let oktaResponseInterceptor: number;

    if (accessToken !== undefined) {
      requestInterceptor = addRequestInterceptor(partnerClient);
      responseInterceptor = addResponseIncerceptor(partnerClient);
      oktaRequestInterceptor = addRequestInterceptor(oktaClient);
      oktaResponseInterceptor = addResponseIncerceptor(oktaClient);
    }

    return () => {
      requestInterceptor !== undefined && removeRequestInterceptor(requestInterceptor, partnerClient);
      responseInterceptor !== undefined && removeResponseInterceptor(responseInterceptor, partnerClient);
      oktaRequestInterceptor !== undefined && removeRequestInterceptor(oktaRequestInterceptor, oktaClient);
      oktaResponseInterceptor !== undefined && removeResponseInterceptor(oktaResponseInterceptor, oktaClient);
    };
  }, [accessToken]);

  return <>{children}</>;
};

export default ClientInterceptors;
